import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../config/apiConfig";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Box,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";

function ForgotPassword() {
  const [form, setForm] = useState({
    email: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.email) {
      setError("Email address is required");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        email: form.email.trim(),
      };

      console.log("Forgot password payload:", payload);

      const res = await axios.post(API.forgotPassword, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.data.success) {
        setSuccess("Password reset code sent! Check your inbox and spam folder.");
        
        // Navigate to verify code screen after 1 second
        setTimeout(() => {
          navigate("/verify-reset-code", { 
            state: { email: form.email.trim() } 
          });
        }, 1000);
      } else {
        setError(res.data.error || "Failed to send reset code. Please try again.");
      }
    } catch (err) {
      console.error("❌ Forgot password error:", err.response?.data || err.message);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "❌ Network error. Please check your connection and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        padding: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Paper 
        elevation={24} 
        sx={{ 
          p: 5, 
          width: "100%", 
          maxWidth: 450,
          borderRadius: 3,
          bgcolor: 'background.paper',
          backdropFilter: 'blur(20px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Stack spacing={3} alignItems="center">
          {/* Header Icon */}
          <LockResetIcon sx={{ fontSize: 60, color: "primary.main" }} />

          {/* Title */}
          <Typography variant="h4" fontWeight="bold" textAlign="center">
            Forgot Password?
          </Typography>

          {/* Description */}
          <Typography variant="body1" color="text.secondary" textAlign="center">
            Enter your email address and we'll send you a verification code to
            reset your password.
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert severity="success" sx={{ width: "100%" }}>
              {success}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            <Stack spacing={3}>
              {/* Email Field */}
              <TextField
                name="email"
                label="Email Address"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                fullWidth
                disabled={loading}
                placeholder="Enter your email address"
                autoComplete="email"
              />

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                fullWidth
                sx={{ py: 1.5 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Send Reset Code"
                )}
              </Button>

              {/* Back to Login */}
              <Button
                variant="text"
                onClick={() => navigate("/login")}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                Back to Login
              </Button>
            </Stack>
          </Box>

          {/* Help Text */}
          <Typography variant="body2" color="text.secondary" textAlign="center">
            If you don't receive an email within a few minutes, please check
            your spam folder or contact support.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}

export default ForgotPassword;
